import { useCallback, useEffect, useState } from 'react';

import { getLogs, GetLogsParams, Log, LogLevel } from '@/lib/api';
import { getStartOfDay, nowJST } from '@/lib/date';

interface UseLogDataProps {
  initialLimit?: number;
  initialOffset?: number;
}

interface UseLogDataReturn {
  logs: Log[];
  loading: boolean;
  error: Error | null;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  filters: {
    level: LogLevel | '';
    since: string;
    until: string;
  };
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  setFilters: (filters: Partial<{ level: LogLevel | ''; since: string; until: string }>) => void;
  fetchLogs: () => Promise<void>;
  resetFilters: () => void;
}

/**
 * Custom hook for fetching and managing log data
 */
export function useLogData({
  initialLimit = 50,
  initialOffset = 0,
}: UseLogDataProps = {}): UseLogDataReturn {
  // Log data
  const [logs, setLogs] = useState<Log[]>([]);
  // Loading state
  const [loading, setLoading] = useState(false);
  // Error
  const [error, setError] = useState<Error | null>(null);
  // Total count
  const [totalCount, setTotalCount] = useState(0);
  // Pagination
  const [page, setPage] = useState(Math.floor(initialOffset / initialLimit));
  const [rowsPerPage, setRowsPerPage] = useState(initialLimit);
  // Filters
  const [filters, setFiltersState] = useState<{
    level: LogLevel | '';
    since: string;
    until: string;
  }>({
    level: LogLevel.INFO, // Default to INFO level
    since: getStartOfDay(nowJST().format()), // Start of today
    until: nowJST().toISOString(), // Current time
  });

  // Update filters
  const setFilters = useCallback(
    (newFilters: Partial<{ level: LogLevel | ''; since: string; until: string }>) => {
      setFiltersState((prev: { level: LogLevel | ''; since: string; until: string }) => ({ ...prev, ...newFilters }));
      // Return to first page when filters change
      setPage(0);
    },
    [],
  );

  // Reset filters
  const resetFilters = useCallback(() => {
    setFiltersState({
      level: '',
      since: '',
      until: '',
    });
    setPage(0);
  }, []);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Build API parameters
      const params: GetLogsParams = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };

      // Add filter conditions
      if (filters.level) {
        params.level = filters.level;
      }

      if (filters.since) {
        params.since = filters.since;
      }

      if (filters.until) {
        params.until = filters.until;
      }

      // API request
      const response = await getLogs(params);
      setLogs(response.data);

      // Set pagination information
      if (response.meta?.pagination) {
        setTotalCount(response.meta.pagination.total);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch logs'));
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  // Refetch logs when page changes
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return {
    logs,
    loading,
    error,
    totalCount,
    page,
    rowsPerPage,
    filters,
    setPage,
    setRowsPerPage,
    setFilters,
    fetchLogs,
    resetFilters,
  };
}
